import { type FC, type ReactNode, useCallback } from 'react';
import { useIdentificationSteps } from '@/account/kyc/useIdentificationSteps';
import { useMerchant } from '@/account/merchant-profile/api';
import { useMe } from '@/account/profile/api';
import { useHasAccessToken, useParsedAccessToken } from '@/auth/api';
import { useLocation } from '@/base/router';
import { useRuntimeEnv } from '@/shared/runtimeEnv';
import type { GateHook } from './hooks';

export const useGate = (): ((hooks: GateHook[]) => boolean) => {
  const runtimeEnv = useRuntimeEnv();
  const hasAccessToken = useHasAccessToken();
  const identificationSteps = useIdentificationSteps();
  const user = useMe();
  const merchant = useMerchant('me');
  const parsedAccessToken = useParsedAccessToken();
  const [location] = useLocation();
  return useCallback(
    (hooks: GateHook[]) =>
      hooks?.every((hook) =>
        hook({
          hasAccessToken,
          user: user.data ?? null,
          runtimeEnv,
          parsedAccessToken: parsedAccessToken ?? null,
          identificationSteps,
          location,
          merchant: merchant.data ?? null,
        }),
      ),
    [
      runtimeEnv,
      hasAccessToken,
      user.data,
      merchant.data,
      parsedAccessToken,
      location,
      identificationSteps,
    ],
  );
};

export const Gate: FC<{
  hooks?: GateHook[];
  children?: ReactNode;
  fallback?: FC;
}> = ({ hooks, children, fallback: Fallback }) => {
  const gate = useGate();
  const isPassed = gate(hooks ?? []);

  if (isPassed) return <>{children}</>;
  return Fallback ? <Fallback /> : null;
};
